import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Download, Eye, Edit, Briefcase } from 'lucide-react';
import toast from 'react-hot-toast';
import { usePagination, useFilters, useSearch, useCrud } from '../../hooks/useApi';
import { catalogService } from '../../services/api';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import EconomicActivityModal from '../../components/Catalog/EconomicActivityModal';
import EconomicActivityTable from '../../components/Catalog/EconomicActivityTable';
import EconomicActivityFilters from '../../components/Catalog/EconomicActivityFilters';
import { downloadJson } from '../../utils/exportHelpers';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../../i18n/userFacingError';
import { isEmptyRichText } from '../../components/RichTextEditor';

const toBool = (v) => v === true || v === 'true';

function mapApiToUi(row) {
  return {
    id: row.id,
    code: row.code,
    name: row.name,
    description: row.description || '',
    isExempt: row.is_exempt ?? row.isExempt,
    isActive: row.is_active ?? row.isActive,
    createdAt: row.created_at || row.createdAt || new Date().toISOString(),
  };
}

const EconomicActivities = () => {
  const { t } = useTranslation('catalog');

  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [viewMode, setViewMode] = useState('table');

  const pagination = usePagination(1, 10);
  const filters = useFilters({
    name: '',
    code: '',
    isExempt: '',
    isActive: '',
  });
  const search = useSearch();

  const { useList, useCreate, useUpdate, useDelete } = useCrud('economic-activities', catalogService.economicActivityCrud);
  const { data: listData, isLoading, error, refetch } = useList();
  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const rawList = useMemo(() => {
    const d = listData;
    return Array.isArray(d) ? d : d?.items ?? d?.data ?? [];
  }, [listData]);

  const mapped = useMemo(() => rawList.map(mapApiToUi), [rawList]);

  const filtered = useMemo(() => {
    let rows = mapped;
    const n = (filters.filters.name || '').trim().toLowerCase();
    if (n) rows = rows.filter((r) => (r.name || '').toLowerCase().includes(n));
    const c = (filters.filters.code || '').trim().toLowerCase();
    if (c) rows = rows.filter((r) => (r.code || '').toLowerCase().includes(c));
    if (filters.filters.isExempt === 'true') rows = rows.filter((r) => r.isExempt === true);
    if (filters.filters.isExempt === 'false') rows = rows.filter((r) => r.isExempt === false);
    if (filters.filters.isActive === 'true') rows = rows.filter((r) => r.isActive === true);
    if (filters.filters.isActive === 'false') rows = rows.filter((r) => r.isActive === false);
    const q = (search.query || '').trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          (r.name || '').toLowerCase().includes(q) ||
          (r.code || '').toLowerCase().includes(q) ||
          (r.description || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [mapped, filters.filters, search.query]);

  useEffect(() => {
    pagination.setTotal(filtered.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length]);

  const paged = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return filtered.slice(start, start + pagination.limit);
  }, [filtered, pagination.page, pagination.limit]);

  const handleCreate = () => {
    setSelected(null);
    setShowModal(true);
  };

  const handleEdit = (row) => {
    setSelected(row);
    setShowModal(true);
  };

  const handleView = (row) => {
    toast(
      [`Código: ${row.code}`, `Nome: ${row.name}`, row.description ? `Descrição: ${row.description}` : '']
        .filter(Boolean)
        .join('\n'),
      { duration: 6000 }
    );
  };

  const handleDelete = (row) => {
    if (!window.confirm('Excluir esta atividade?')) return;
    deleteMutation.mutate(row.id, {
      onSuccess: () => {
        setShowModal(false);
        setSelected(null);
      },
      onError: (e) => toast.error(userFacingError(e, 'deleteError')),
    });
  };

  const handleSave = async (data) => {
    const payload = {
      code: data.code,
      name: data.name,
      description: isEmptyRichText(data.description) ? null : data.description,
      is_exempt: toBool(data.isExempt),
      is_active: toBool(data.isActive),
    };
    if (selected) {
      await updateMutation.mutateAsync({ id: selected.id, data: payload });
      setShowModal(false);
      setSelected(null);
      return;
    }
    const { is_active: _unused, ...createPayload } = payload;
    await createMutation.mutateAsync(createPayload);
    setShowModal(false);
  };

  const handleExport = () => {
    downloadJson(`atividades-economicas-${new Date().toISOString().slice(0, 10)}.json`, filtered);
    toast.success('Exportação iniciada');
  };

  const getStatusBadge = (isActive) => (
    <span className={`badge ${isActive ? 'badge-success' : 'badge-danger'}`}>{isActive ? 'Ativo' : 'Inativo'}</span>
  );

  const getExemptBadge = (isExempt) => (
    <span className={`badge ${isExempt ? 'badge-warning' : 'badge-info'}`}>{isExempt ? 'Isento' : 'Tributável'}</span>
  );

  if (error) {
    return (
      <FormPageShell>
        <div className="rounded-lg border border-danger-200 bg-danger-50 p-4 text-danger-800">
          {t('common:loadError')}{' '}
          <button type="button" className="btn btn-outline btn-sm ml-2" onClick={() => refetch()}>
            Tentar novamente
          </button>
        </div>
      </FormPageShell>
    );
  }

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("activitiesTitle")}
          description="CNAE e flags de isenção para o catálogo."
          breadcrumbs={[{ label: 'Início', to: getAppHomePath() }, { label: 'Catálogo' }]}
        >
          <Can permission={P.CATALOG_ACCESS}>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button type="button" className="btn btn-outline shadow-sm" onClick={handleExport} disabled={!filtered.length}>
                <Download className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Exportar</span>
              </button>
              <button type="button" onClick={handleCreate} className="btn btn-primary inline-flex items-center gap-2 shadow-sm">
                <Plus className="h-4 w-4" />
                Nova atividade
              </button>
            </div>
          </Can>
        </PageHeader>

        <div className="data-list-card">
          <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
            <EconomicActivityFilters filters={filters} search={search} />
          </div>

          <div className="space-y-4 p-5 sm:p-6">
            {isLoading && <p className="text-sm text-gray-600">Carregando…</p>}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Tabela
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('cards')}
                  className={`btn ${viewMode === 'cards' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Cards
                </button>
              </div>
              <div className="text-sm text-gray-600">Mostrando {filtered.length} atividade(s)</div>
            </div>

            {viewMode === 'table' ? (
              <EconomicActivityTable
                economicActivities={paged}
                totalCount={filtered.length}
                pagination={pagination}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
                getStatusBadge={getStatusBadge}
                getExemptBadge={getExemptBadge}
                onCreate={handleCreate}
              />
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {paged.map((economicActivity) => (
                  <div key={economicActivity.id} className="card">
                    <div className="card-content">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                            <Briefcase className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{economicActivity.name}</h3>
                            <p className="text-sm text-gray-600">{economicActivity.code}</p>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          {getStatusBadge(economicActivity.isActive)}
                          {getExemptBadge(economicActivity.isExempt)}
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="text-sm text-gray-700">{economicActivity.description}</div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Criado em:</span>
                          <span className="text-gray-900">
                            {new Date(economicActivity.createdAt).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </div>

                      <Can permission={P.CATALOG_ACCESS}>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => handleView(economicActivity)} className="btn btn-outline btn-sm flex-1">
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </button>
                          <button type="button" onClick={() => handleEdit(economicActivity)} className="btn btn-outline btn-sm flex-1">
                            <Edit className="w-4 h-4 mr-1" />
                            Editar
                          </button>
                        </div>
                      </Can>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <EconomicActivityModal
          key={selected?.id ?? 'new'}
          economicActivity={selected}
          onClose={() => {
            setShowModal(false);
            setSelected(null);
          }}
          onSave={handleSave}
        />
      )}
    </FormPageShell>
  );
};

export default EconomicActivities;
